// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

import { useEffect, useState } from "react";
import { App, Button, Input, List, Popconfirm, Space, Typography } from "antd";
import { KeyOutlined, PlusOutlined } from "@ant-design/icons";
import { Homeserver } from "../api";
import type { PasskeyInfo, Session } from "../types";
import { passkeySupported, registerPasskey } from "../passkey";

const hs = new Homeserver();

// PasskeyPanel manages the user's WebAuthn credentials: add a passkey / security
// key (so they can sign in without a password), list them, and remove them.
export function PasskeyPanel({ session }: { readonly session: Session }) {
  const { message } = App.useApp();
  const [keys, setKeys] = useState<PasskeyInfo[]>([]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  async function refresh() {
    try {
      setKeys(await hs.webauthnCredentials(session.access_token));
    } catch {
      // best-effort
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.access_token]);

  async function add() {
    setBusy(true);
    try {
      await registerPasskey(session.access_token, name.trim() || "Passkey");
      setName("");
      message.success("Passkey added — you can now sign in with it");
      await refresh();
    } catch (e) {
      message.error(`Could not add passkey: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    try {
      await hs.deleteWebauthnCredential(session.access_token, id);
      await refresh();
    } catch (e) {
      message.error(`Could not remove: ${(e as Error).message}`);
    }
  }

  if (!passkeySupported()) {
    return (
      <Typography.Text type="secondary">
        Passkeys aren’t available in this browser/app. Open Zalmoxis in a desktop or mobile browser to add one.
      </Typography.Text>
    );
  }

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
        A passkey (security key, fingerprint, or device PIN) lets you sign in without a password. Add one here,
        then use “Sign in with a passkey” on the login screen.
      </Typography.Paragraph>

      <Space.Compact style={{ width: "100%" }}>
        <Input placeholder="Name (e.g. YubiKey, this laptop)" value={name} onChange={(e) => setName(e.target.value)} maxLength={40} />
        <Button type="primary" icon={<PlusOutlined />} loading={busy} onClick={add} className="passkey-add">
          Add passkey
        </Button>
      </Space.Compact>

      <List
        size="small"
        header={<Typography.Text strong>Your passkeys</Typography.Text>}
        locale={{ emptyText: "No passkeys yet" }}
        dataSource={keys}
        renderItem={(k) => (
          <List.Item
            actions={[
              <Popconfirm key="rm" title="Remove this passkey?" onConfirm={() => remove(k.id)} okText="Remove" okButtonProps={{ danger: true }}>
                <Button size="small" danger type="text">
                  Remove
                </Button>
              </Popconfirm>,
            ]}
          >
            <Space>
              <KeyOutlined />
              <Typography.Text>{k.name || "Passkey"}</Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                added {new Date(k.created_at).toLocaleDateString()}
              </Typography.Text>
            </Space>
          </List.Item>
        )}
      />
    </Space>
  );
}
